import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Settings, 
  LogOut, 
  Home,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle,
  XCircle,
  Send,
  Gavel,
  Clock,
  Archive,
} from "lucide-react";
import defaultAvatar from "@/assets/default-avatar.png";
import temwiseLogo from "@/assets/temwise-logo.png";
import temwiseIcon from "@/assets/temwise-icon.png";

interface SupplierSidebarProps {
  className?: string;
}

const supplierNavItems = [
  { name: "Active RFPs", path: "/supplier/active", icon: FileText },
  { name: "Submitted Proposals", path: "/supplier/submitted", icon: Send },
  { name: "Live Auctions", path: "/supplier/live-auctions", icon: Gavel },
  { name: "Closed Auctions", path: "/supplier/closed-auctions", icon: Clock },
  { name: "Successful Proposals", path: "/supplier/successful", icon: CheckCircle },
  { name: "Unsuccessful Proposals", path: "/supplier/unsuccessful", icon: XCircle },
  { name: "Inactive", path: "/supplier/inactive", icon: Archive },
];

const SupplierSidebar = ({ className }: SupplierSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate("/signin");
  };

  const isActive = (path: string) => location.pathname === path;

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const userName = user?.user_metadata?.name || user?.email?.split("@")[0] || "User";

  return (
    <div
      className={cn(
        "relative flex flex-col h-screen transition-all duration-300 ease-in-out overflow-hidden",
        collapsed ? "w-[72px]" : "w-64",
        className
      )}
      style={{ backgroundColor: "#040435" }}
    >
      {/* Logo - centered when expanded */}
      <div className={cn(
        "flex items-center h-20 transition-all duration-300 shrink-0 justify-center",
        collapsed ? "px-2" : "px-6"
      )}>
        {collapsed ? (
          <img src={temwiseIcon} alt="Temwise" className="h-10 w-10" />
        ) : (
          <img src={temwiseLogo} alt="Temwise" className="h-8" />
        )}
      </div>
      
      {/* Navigation with custom scrollbar */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 sidebar-scroll">
        {/* Dashboard Button */}
        <Link to="/supplier/dashboard">
          <div
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg mb-4 transition-all duration-200",
              collapsed ? "justify-center" : "",
              isActive("/supplier/dashboard")
                ? "bg-[#3B82F6] text-white"
                : "text-white/80 hover:bg-white/10"
            )}
          >
            <Home className="h-4 w-4 shrink-0" />
            {!collapsed && (
              <span className="text-sm font-medium whitespace-nowrap">Dashboard</span>
            )}
          </div>
        </Link>

        {/* Divider */}
        <div className="px-3 my-3">
          <div className="h-px bg-white/10" />
        </div>

        {/* Navigation Items */}
        <div className="space-y-0.5">
          {supplierNavItems.map((item) => {
            const ItemIcon = item.icon;
            const isItemActive = isActive(item.path);
            
            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200",
                    collapsed ? "justify-center" : "",
                    isItemActive
                      ? "bg-[#3B82F6] text-white"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  )}
                >
                  <ItemIcon className="h-4 w-4 shrink-0" />
                  {!collapsed && (
                    <span className="text-sm whitespace-nowrap">{item.name}</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </nav>
      
      {/* Bottom Section */}
      <div className="py-4 px-2 shrink-0">
        {/* Settings */}
        <button
          onClick={() => navigate('/supplier/settings')}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center" : ""
          )}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="text-sm whitespace-nowrap">Settings</span>
          )}
        </button>
        
        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center" : ""
          )}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && (
            <span className="text-sm whitespace-nowrap">Sign Out</span>
          )}
        </button>

        {/* Avatar with User Info */}
        <div className={cn(
          "flex items-center gap-3 mt-4 px-2",
          collapsed ? "justify-center" : ""
        )}>
          <Avatar className="h-10 w-10 ring-2 ring-green-500 ring-offset-2 ring-offset-[#040435] shrink-0">
            <AvatarImage src={defaultAvatar} alt={userName} />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm font-medium">
              {getInitials(userName)}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{userName}</p>
              <p className="text-xs text-white/50 truncate">Supplier</p>
            </div>
          )}
        </div>

        {/* Collapse Toggle Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 mt-4 rounded-lg transition-all duration-200 text-white/70 hover:bg-white/10 hover:text-white",
            collapsed ? "justify-center" : ""
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4 shrink-0" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4 shrink-0" />
              <span className="text-sm whitespace-nowrap">Collapse</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SupplierSidebar;
